import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Send, Check, AlertCircle, Loader2 } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setStatus({ state: "error", msg: t("contact.form.errorRequired") });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      await axios.post(`${API}/contact`, payload, { timeout: 15000 });
      setStatus({ state: "success", msg: t("contact.form.success") });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || t("contact.form.errorGeneric");
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : t("contact.form.errorGeneric") });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 overflow-hidden">
      <div className="blob blob-2" style={{ width: 500, height: 500, top: "-100px", right: "-180px", opacity: 0.2 }} data-parallax="0.06" />
      <div className="blob blob-1" style={{ width: 400, height: 400, bottom: "-80px", left: "-120px", opacity: 0.25 }} data-parallax="-0.05" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-5">
            <div className="reveal flex items-center gap-3 mb-5">
              <span className="h-[1px] w-10 bg-[#0F0524]/40" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#5C527A] font-semibold">
                {t("contact.eyebrow")}
              </span>
            </div>
            <h2 className="reveal font-display text-5xl md:text-6xl font-extralight leading-[0.95] tracking-[-0.04em] text-[#0F0524]">
              {t("contact.titleA")} <br />
              <span className="italic grad-text">{t("contact.titleB")}</span>
            </h2>
            <p className="reveal mt-6 text-[15px] leading-relaxed text-[#5C527A] max-w-md">
              {t("contact.desc")}
            </p>

            <div className="mt-10 space-y-4">
              <a href="mailto:lionsoftware@mmaarchuuk.com" data-testid="contact-email-link" className="reveal group flex items-center justify-between glass rounded-2xl p-5">
                <div className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-xl iridescent grid place-items-center">
                    <Mail className="h-5 w-5 text-[#0F0524]" />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C527A] font-semibold">
                      {t("contact.emailLabel")}
                    </div>
                    <div className="mt-1 font-display text-[15px] text-[#0F0524]">
                      lionsoftware@mmaarchuuk.com
                    </div>
                  </div>
                </div>
                <span className="text-[#A49BFF] group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">→</span>
              </a>

              <a href="tel:045771388" data-testid="contact-phone-link" className="reveal group flex items-center justify-between glass rounded-2xl p-5">
                <div className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-xl iridescent grid place-items-center">
                    <Phone className="h-5 w-5 text-[#0F0524]" />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C527A] font-semibold">
                      {t("contact.phoneLabel")}
                    </div>
                    <div className="mt-1 font-display text-[15px] text-[#0F0524]" dir="ltr">(04) 577 1388</div>
                  </div>
                </div>
                <span className="text-[#A49BFF] group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform">→</span>
              </a>

              <a href="https://maps.google.com/?q=Tamani+Arts+Building+Al+Asayel+Street+Business+Bay+Dubai" target="_blank" rel="noopener noreferrer" data-testid="contact-address-link" className="reveal group flex items-start justify-between glass rounded-2xl p-5">
                <div className="flex items-start gap-4">
                  <span className="h-11 w-11 shrink-0 rounded-xl iridescent grid place-items-center">
                    <MapPin className="h-5 w-5 text-[#0F0524]" />
                  </span>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C527A] font-semibold">
                      {t("contact.addressLabel")}
                    </div>
                    <div className="mt-1 font-display text-[14px] text-[#0F0524] leading-snug">
                      {t("contact.addressLine1")}
                      <br />
                      {t("contact.addressLine2")}
                    </div>
                  </div>
                </div>
                <span className="text-[#A49BFF] group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform mt-1">→</span>
              </a>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" noValidate className="reveal glass rounded-3xl p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t("contact.form.name")} testId="field-name">
                <input data-testid="input-name" type="text" value={form.name} onChange={onChange("name")} required className="input-underline" placeholder={t("contact.form.namePh")} />
              </Field>
              <Field label={t("contact.form.email")} testId="field-email">
                <input data-testid="input-email" type="email" value={form.email} onChange={onChange("email")} required className="input-underline" placeholder={t("contact.form.emailPh")} />
              </Field>
              <Field label={t("contact.form.phone")} testId="field-phone">
                <input data-testid="input-phone" type="tel" value={form.phone} onChange={onChange("phone")} className="input-underline" placeholder={t("contact.form.phonePh")} />
              </Field>
              <Field label={t("contact.form.subject")} testId="field-subject">
                <input data-testid="input-subject" type="text" value={form.subject} onChange={onChange("subject")} required className="input-underline" placeholder={t("contact.form.subjectPh")} />
              </Field>
              <Field label={t("contact.form.message")} testId="field-message" full>
                <textarea data-testid="input-message" value={form.message} onChange={onChange("message")} required rows={5} className="input-underline resize-none" placeholder={t("contact.form.messagePh")} />
              </Field>

              <div className="md:col-span-2 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 pt-2">
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={status.state === "loading"}
                  className="btn-sheen inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 text-[14px] font-semibold text-white disabled:opacity-70"
                  style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}
                >
                  {status.state === "loading" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t("contact.form.sending")}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      {t("contact.form.submit")}
                    </>
                  )}
                </button>

                {status.state === "success" && (
                  <div data-testid="contact-success" className="flex items-center gap-2 text-[13px] text-[#0F0524]">
                    <span className="h-6 w-6 rounded-full iridescent grid place-items-center">
                      <Check className="h-3.5 w-3.5 text-[#0F0524]" />
                    </span>
                    {status.msg}
                  </div>
                )}
                {status.state === "error" && (
                  <div data-testid="contact-error" className="flex items-center gap-2 text-[13px] text-[#B00050]">
                    <AlertCircle className="h-4 w-4" />
                    {status.msg}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input-underline { width: 100%; background: transparent; border: 0; border-bottom: 1px solid rgba(15,5,36,0.15); padding: 14px 2px 10px 2px; font-family: 'Manrope', system-ui, sans-serif; font-size: 15px; color: #0F0524; outline: none; transition: border-color 0.3s, padding 0.3s; }
        .input-underline::placeholder { color: rgba(92,82,122,0.55); }
        .input-underline:focus { border-bottom-color: transparent; background-image: linear-gradient(#fff, #fff), linear-gradient(90deg, #00F0FF, #A49BFF, #FF00E5); background-origin: border-box; background-clip: padding-box, border-box; border-bottom: 2px solid transparent; }
      `}</style>
    </section>
  );
}

function Field({ label, testId, full, children }) {
  return (
    <label data-testid={testId} className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#5C527A] font-semibold">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
